import React from "react";
import { useTranslation } from "react-i18next";
import { Standing } from "../../../models/Standing";
import { getStandingZone, StandingZone, ZONE_COLORS, ZONE_LABEL_KEYS } from "../../../utils/standingZones";
import { Column, colorVars } from "./contentTeamShared";
import DeviationChip, { CheckIcon } from "./DeviationChip";
import styles from "./ContentTeam.module.css";

export interface TableSort {
  /** "rank", or a column key. */
  by: string;
  dir: 1 | -1;
}

interface Props {
  season: number;
  division: string;
  divisionName: string;
  standings: Standing[];
  columns: Column[];
  played: boolean;
  sort: TableSort;
  onSort: (by: string) => void;
  selectedTeamId: string | undefined;
  onSelect: (teamId: string) => void;
}

const ZONE_ORDER: Exclude<StandingZone, "white">[] = ["green", "yellow", "orange", "red"];

const Arrow: React.FC<{ dir: 1 | -1 }> = ({ dir }) => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points={dir === 1 ? "18 15 12 9 6 15" : "6 9 12 15 18 9"} />
  </svg>
);

/** The division's current table with every predictor's guess beside each team. Rows select a team. */
const ComparisonTable: React.FC<Props> = ({
  season, division, divisionName, standings, columns, played, sort, onSort, selectedTeamId, onSelect,
}) => {
  const { t } = useTranslation("pages");

  const zones = standings.map((s, index) => getStandingZone(season, division, index, s));
  const order = standings.map((_, index) => index);
  if (sort.by !== "rank") {
    const column = columns.find((c) => c.key === sort.by);
    const key = (index: number) => column?.ranks?.get(standings[index].team.teamId) ?? 99;
    order.sort((x, y) => key(x) - key(y) || x - y);
  }
  if (sort.dir === -1) order.reverse();

  const legend = ZONE_ORDER.filter((z) => zones.includes(z)).map((z) => ({
    zone: z,
    color: ZONE_COLORS[z],
    // Season 33+ Ligaen: the green places are the LAN spots, not a promotion.
    label: z === "green" && division === "ligaen" && season >= 33 ? t("predict.zoneLan") : t(ZONE_LABEL_KEYS[z]),
  }));
  const games = Math.round(standings.reduce((sum, s) => sum + s.gamesPlayed, 0) / 2);
  const gridStyle = { "--cols": columns.length } as React.CSSProperties;

  return (
    <section className={`${styles.panel} ${styles.tablePanel}`} aria-label={t("contentTeam.tableTitle", { division: divisionName })}>
      <div className={styles.strip}>
        <h2 className={styles.stripTitle}>{t("contentTeam.tableTitle", { division: divisionName })}</h2>
        <span className={styles.muted}>{played ? t("contentTeam.gamesPlayed", { count: games }) : t("contentTeam.noGames")}</span>
      </div>
      <div className={`${styles.grid} ${styles.headRow}`} style={gridStyle}>
        <button
          type="button"
          className={`${styles.sortBtn} ${sort.by === "rank" ? styles.sortBtnOn : ""}`}
          onClick={() => onSort("rank")}
          aria-label={t("contentTeam.sortRank")}
        >
          #{sort.by === "rank" && <Arrow dir={sort.dir} />}
        </button>
        <span style={{ paddingLeft: 4 }}>{t("contentTeam.team")}</span>
        {columns.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`${styles.sortBtn} ${sort.by === c.key ? styles.sortBtnOn : ""}`}
            style={colorVars(c.color)}
            onClick={() => onSort(c.key)}
            aria-label={t("contentTeam.sortBy", { name: c.name })}
          >
            <span className={styles.sortBtnName}>{c.name}</span>
            {sort.by === c.key && <Arrow dir={sort.dir} />}
          </button>
        ))}
      </div>
      <div>
        {order.map((index) => {
          const s = standings[index];
          const on = s.team.teamId === selectedTeamId;
          const zone = zones[index];
          return (
            <button
              key={s.team.teamId}
              type="button"
              className={`${styles.grid} ${styles.row} ${on ? styles.rowOn : ""}`}
              style={gridStyle}
              aria-pressed={on}
              onClick={() => onSelect(s.team.teamId)}
            >
              <span className={styles.rankCell} style={zone !== "white" ? { background: ZONE_COLORS[zone] } : undefined}>
                {s.rank}
              </span>
              <span className={styles.teamCell}>
                <img className={styles.teamLogo} src={s.team.teamLogo} alt="" loading="lazy" />
                <span className={styles.teamText}>
                  <span className={styles.teamName}>{s.team.teamName}</span>
                  <span className={styles.teamRecord}>
                    {t("contentTeam.record", { wins: s.wins, losses: s.gamesPlayed - s.wins, points: s.points })}
                  </span>
                </span>
              </span>
              {columns.map((c) => {
                const predicted = c.ranks?.get(s.team.teamId);
                return (
                  <span key={c.key} className={styles.guessCell}>
                    {predicted === undefined ? (
                      <span className={styles.none}>–</span>
                    ) : (
                      <>
                        <span className={styles.guessRank}>{predicted}</span>
                        {played && <DeviationChip predicted={predicted} actual={s.rank} />}
                      </>
                    )}
                  </span>
                );
              })}
            </button>
          );
        })}
      </div>
      <div className={styles.tableFoot}>
        <span className={styles.legend}>
          {legend.map((z) => (
            <span key={z.zone} className={styles.legendItem}>
              <span className={styles.swatch} style={{ background: z.color }} />
              {z.label}
            </span>
          ))}
        </span>
        {played && (
          <span className={styles.legend}>
            <span className={styles.legendItem}>
              <span className={`${styles.chip} ${styles.chipExact}`} style={{ minWidth: 20, height: 16 }}>
                <CheckIcon />
              </span>
              {t("contentTeam.exactLegend")}
            </span>
            <span>{t("contentTeam.deviationHelp")}</span>
          </span>
        )}
      </div>
    </section>
  );
};

export default ComparisonTable;
